import { compile } from 'mathjs';
import logger from 'src/utils/logger';

export const getFixedValues = (fixedColumns, fixedValues) => {
  const result = {};

  const fixedColumnNames = fixedColumns.map(c => c.name);

  fixedValues.forEach(fixedValue => {
    if (fixedColumnNames.indexOf(fixedValue.columnName) === -1) {
      return;
    }
    result[fixedValue.columnName] = result[fixedValue.columnName] || [];
    result[fixedValue.columnName].push(fixedValue.value);
  });

  return result;
};

export const resultUnique = that => {
  const a = that.concat();
  for (let i = 0; i < a.length; i += 1) {
    for (let j = i + 1; j < a.length; j += 1) {
      if (a[i].datasheetQuestionId === a[j].datasheetQuestionId)
        a.splice(j, 1);
    }
  }

  return a;
};

export const prepareRuleAndGetExclusive = rule => {
  const regexp = /EXCLUSIVE\[(?<exclusive>([0-9]+,?)+)\]/g;
  const exclusive = (regexp.exec(rule)?.groups?.exclusive?.split(',') || []).map(c => +c - 1);

  return {
    rule: rule.replace(regexp, 'true'),
    exclusive,
  };
};

export const isMandatoryQuestion = (question) => {
  const validations = question.validations || [];
  const hasNullRule = validations.some(({ rule }) => 
    rule.includes('null') || rule.includes('NULL')
  );
  return hasNullRule;
};


export const getValidationErrors = (question, variables, fromJob = true) => {
  const questionValue = variables[`Q${question.datasheetQuestionId}`] !== undefined ? variables[`Q${question.datasheetQuestionId}`] : null;
  const attachmentsValue = variables[`ATT${question.datasheetQuestionId}`] !== undefined ? variables[`ATT${question.datasheetQuestionId}`] : null;
  const externalAttachmentsValue = variables[`EXTATT${question.datasheetQuestionId}`] !== undefined ? variables[`EXTATT${question.datasheetQuestionId}`] : null;
  let isMandatory=false;
  const hasNullValidations = (question.validations || []).filter(({ rule }) => {
    const partsToSearch = [
      'null',
      'NULL',
      'attachments',
      'external_attachments',
      'regular_attachments',
    ];
    let result = false;
    for (let i = 0; i < partsToSearch.length; i+=1) {
      const partToSearch = partsToSearch[i];
      if (rule.includes(partToSearch)) {
        result = true;
        if((partToSearch===partsToSearch[0] || partToSearch===partsToSearch[1]) && !isMandatory && (questionValue === null || Number.isNaN(questionValue))){
          isMandatory=true;
          break;
        }
        isMandatory=false;
      }
    }
    return result;
  }).length > 0;

  if (!hasNullValidations && (questionValue === null || Number.isNaN(questionValue))) {
    return [];
  }

  const questionValueCount = Array.isArray(questionValue) ? questionValue.length : 1;

  const isExclusiveSelected = (question.validations || []).filter(({ rule }) => {
    const { exclusive } = prepareRuleAndGetExclusive(rule);
    if (exclusive.length > 0) {
      const questionValueArray = Array.isArray(questionValue) ? questionValue : [questionValue];
      return questionValueArray.some(c => exclusive.includes(c));
    }
    return false;
  }).length > 0;

  return (question.validations || [])
    .filter(({ rule }) => {
      try {
        const { rule: preparedRule, exclusive } = prepareRuleAndGetExclusive(rule);

        if ((isExclusiveSelected && preparedRule.toLowerCase().indexOf('count') !== -1) || (exclusive.length > 0)) {
          return false;
        }

        const expression = compile(preparedRule);
        return !expression.evaluate({
          this: Array.isArray(questionValue) ? questionValue.join(',') : questionValue,
          count: questionValueCount,
          Count: questionValueCount,
          NULL: null,
          attachments: (attachmentsValue || externalAttachmentsValue) ? true : null,
          external_attachments: externalAttachmentsValue,
          regular_attachments: attachmentsValue,
          ...variables,
        });
      } catch (e) {
        return true;
      }
    })
    .map(({ text }) => isMandatory && fromJob?"mandatory":text);
};

export const getDescendantQuestion = (section, questions) => {
  let result = questions ? [...questions] : [];
  result = result.concat(
    section.children.map(c => getDescendantQuestion(c).flat()).flat(),
  );
  return result;
};

export const convertNumberToPrecision = (v, precision) => {
  if (v === '') {
    return null;
  }

  let i = 0;
  // eslint-disable-next-line no-plusplus
  const vDot = v.replace(/,/g, '.').replace(/[.]/g, m => (!i++ ? m : ''));

  if (!vDot.match(new RegExp('^-?[0-9]*\\.?[0-9]*$'))) {
    return '';
  }

  if (vDot === '-') {
    return vDot;
  }

  const regexp =
    precision === 0
      ? new RegExp('^(-?[0-9]*)')
      : new RegExp(`^(-?([0-9]*)\\.?[0-9]{1,${precision}})`);

  const matches = vDot.match(regexp);
  if (!matches) {
    return null;
  }

  const parts = vDot.split('.');
  if (parts[1] && parts[1].length > precision) {
    return +matches[0];
  }

  if (vDot.match(new RegExp('(\\.|0)$'))) {
    return vDot;
  }

  return +matches[0];
};

export const convertPercentToPrecision = v => {
  if (v === '') {
    return null;
  }

  let i = 0;
  // eslint-disable-next-line no-plusplus
  const vDot = v.replace(/,/g, '.').replace(/[.]/g, m => (!i++ ? m : ''));

  if (!vDot.match(new RegExp('^[0-9]*\\.?[0-9]*$'))) {
    return '';
  }

  const regexp = new RegExp('^(([0-9]*)\\.?[0-9]+)');

  const matches = vDot.match(regexp);
  if (!matches) {
    return null;
  }

  if (vDot.match(new RegExp('(\\.|0)$'))) {
    return vDot;
  }

  return Math.min(100, Math.max(0, +matches[0]));
};

export const commaSeparatedThousandNumbers = (str) => {
  try {
    if (str) {
      let t = 0;
      const strWithOnlyNumericAndDecimalPoints = str.replace(/[^0-9.]+/g, '');
      const strWithoutMultipleDecimalPoints = strWithOnlyNumericAndDecimalPoints.replace(/\./g, match => {
        t += 1;
        return t > 1 ? '' : match;
      });

      const strIfContainsOnlyDecimalPoint = strWithoutMultipleDecimalPoints === '.';

      let finalStr = '';
      if (!strIfContainsOnlyDecimalPoint) {
        finalStr = strWithoutMultipleDecimalPoints;
        const containsDecimalPoint = finalStr.includes('.');
        const splitFinalStr = finalStr.split('.');
        const numberStr = splitFinalStr[0];
        const decimalNumber = splitFinalStr.length > 1 ? finalStr.split('.')[1] : '';
        const thousandValueSeparatedByComma = Number(numberStr).toLocaleString('en-Us') ;
        return containsDecimalPoint ? `${thousandValueSeparatedByComma}.${decimalNumber}` : thousandValueSeparatedByComma;
      }
      finalStr = '0.';
      return finalStr;

    }
    return '';

  } catch (err) {
    logger.error(err);
    return '';
  }
};

export const getSectionLevelClassName = (level) => {
  if (level === 0) return 'level-1';
  if (level === 1) return 'level-2';
  return 'level-3';
};